import { cva } from 'class-variance-authority'
import Markdown from 'react-markdown'
import { ComponentProps, CSSProperties, PropsWithChildren } from 'react'
import { VariantProps } from 'class-variance-authority'
import { cn, rem } from '@/lib/utils'

export type TextVariant =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
type BodyVariant = 'default' | 'secondary' | 'card'

export type TextBaseProps = PropsWithChildren<VariantProps<typeof textVariants>>

export interface TextProps extends TextBaseProps {
  as?: TextVariant
  styleAs?: TextVariant
  body?: BodyVariant
  style?: CSSProperties
  className?: string
  title?: string
  size?: number
  lineHeight?: number
  markdownComponents?: ComponentProps<typeof Markdown>['components']
}

export const textVariants = cva([], {
  variants: {
    as: {
      h1: 'scroll-m-20 font-serif text-4xl leading-9.5 sm:text-6xl sm:leading-15 md:text-7xl md:leading-19',
      h2: 'scroll-m-20 font-serif text-3xl leading-8.75 sm:text-5xl sm:leading-12 md:text-6xl md:leading-15',
      h3: 'scroll-m-20 font-serif text-2xl leading-7.75 sm:text-4xl sm:leading-9.5 md:text-5xl md:leading-12',
      h4: 'scroll-m-20 font-serif text-xl leading-6.25 sm:text-2xl sm:leading-7.75 md:text-4xl md:leading-9.5',
      h5: 'scroll-m-4 text-base font-medium leading-5 sm:text-xl sm:leading-6.25 md:text-2xl md:leading-7.75',
      h6: 'scroll-m-4 text-base leading-5 tracking-[calc(1/16*1em)] sm:tracking-[calc(3/16*1em)] md:text-xl',
      p: 'text-sm leading-4.5 sm:text-base sm:leading-5',
      div: 'text-sm leading-4.5 sm:text-base sm:leading-5',
      span: false,
    },
    variant: {
      primary: 'text-sm leading-6 sm:text-base sm:leading-6.5',
      secondary: 'text-xs leading-3.75 sm:text-sm sm:leading-5',
      card: 'text-xs leading-3.75',
      link: 'text-xs leading-3.75 underline decoration-[0.7px] underline-offset-2 sm:text-base sm:leading-6.5',
      label: 'text-sm leading-4.5',
      button: 'text-base font-medium leading-5',
    },
    body: {
      default: '',
      secondary: '',
      card: '',
    },
    fontWeight: {
      default: [],
      light: 'font-light',
      normal: 'font-normal',
      medium: 'font-medium',
      bold: 'font-bold',
      extrabold: 'font-extrabold',
      semiBold: 'font-semibold',
    },
    capitalize: {
      all: 'capitalize',
      first: 'capitalize-first block',
      none: 'normal-case',
    },
    uppercase: {
      true: 'uppercase',
    },
    underline: {
      // true: 'underline underline-offset-2',
      true: 'border-b-[0.7px] border-solid border-secondary',
    },
    muted: {
      true: 'text-muted-foreground',
    },
    markdown: {
      true: 'font-normal',
    },
  },
  compoundVariants: [
    {
      as: 'p',
      body: 'secondary',
      className: 'text-sm leading-4.5',
    },
    {
      as: 'p',
      body: 'card',
      className: 'text-xs !leading-3.75',
    },
  ],
  defaultVariants: {
    fontWeight: 'default',
    markdown: false,
  },
})

const Text = ({
  as = 'span',
  styleAs = as,
  variant,
  children,
  className,
  style,
  title,
  markdown,
  capitalize,
  size,
  lineHeight,
  markdownComponents,
  ...variantProps
}: TextProps) => {
  const calculatedClassName = cn(
    textVariants({
      as: styleAs,
      variant,
      capitalize,
      ...variantProps,
      markdown,
    }),
    className,
  )

  const Component = as
  const isMarkdown = markdown && typeof children === 'string'

  return (
    <Component
      className={calculatedClassName}
      title={title}
      style={{
        ...(size ? { fontSize: rem(size), lineHeight: 1 } : {}),
        ...(lineHeight ? { lineHeight: rem(lineHeight) } : {}),
        ...style,
      }}
    >
      {isMarkdown ? (
        <Markdown components={markdownComponents}>{children}</Markdown>
      ) : (
        children
      )}
    </Component>
  )
}

export default Text
